/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TenantDomainSelector.tsx
 🗓️ Created: 2025-10-08
 🧩 Description:
    Component for selecting active tenant and telephony domain.
    Displays in the header to provide clear context for telephony operations.

 🔧 Notes:
    - Shows current tenant and domain selection
    - Allows switching between multiple tenants (if user has access)
    - Allows switching between domains within a tenant
    - Persists selection in context store
    - Updates API context for telephony operations

 🔁 Last Updated: 2025-10-08 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Building2, Server, Search, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../store/auth';
import { useContextStore, TenantInfo, DomainInfo } from '../store/context';
import { buildApiUrl, getApiHeaders } from '../config/api';
import { useI18n } from '../hooks/useI18n';
import { SimpleTooltip } from './ui/SimpleTooltip';

export default function TenantDomainSelector() {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const {
    selectedTenant,
    selectedDomain,
    availableTenants,
    availableDomains,
    setSelectedTenant,
    setSelectedDomain,
    setAvailableTenants,
    setAvailableDomains,
    initializeFromUser
  } = useContextStore();

  const [showMenu, setShowMenu] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const avatarRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Track if we've already loaded data to prevent duplicate API calls
  const tenantsLoadedRef = useRef(false);
  const domainsLoadedRef = useRef<string | null>(null);

  // Initialize from user on mount
  useEffect(() => {
    if (user && !tenantsLoadedRef.current) {
      initializeFromUser(user);

      // Load user's tenants only once
      loadUserTenants();
      tenantsLoadedRef.current = true;
    }
  }, [user?.id]);

  // Load domains when tenant changes OR when availableDomains is empty (e.g. fresh page load with persisted tenant)
  useEffect(() => {
    const tenantId = selectedTenant?.id;
    if (tenantId && (domainsLoadedRef.current !== tenantId || availableDomains.length === 0)) {
      loadTenantDomains();
      domainsLoadedRef.current = tenantId;
    }
  }, [selectedTenant?.id, availableDomains.length]);

  const loadUserTenants = async () => {
    try {
      // Get all tenants associated with this user from admin database
      const url = buildApiUrl(`/api/tenants?page=1&limit=100&sortBy=name&sortOrder=asc`);
      const headers = getApiHeaders('main', user?.id); // Use main as default tenant for API calls

      const response = await fetch(url, { headers });

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const tenants: TenantInfo[] = result.data.map((t: any) => ({
            id: t.id,
            name: t.name,
            slug: t.slug
          }));

          setAvailableTenants(tenants);

          if (!selectedTenant && tenants.length > 0) {
            setSelectedTenant(tenants[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user tenants:', error);
    }
  };

  const loadTenantDomains = async () => {
    if (!selectedTenant) {
      return;
    }

    try {
      setLoadingDomains(true);

      // Use tenantSlug - backend will lookup the INTEGER id internally
      const url = buildApiUrl(`/api/telephony/domains?page=1&limit=100&sortBy=domain_name&sortOrder=asc&tenantSlug=${selectedTenant.slug}`);
      const headers = getApiHeaders(selectedTenant.slug, user?.id);

      const response = await fetch(url, { headers });

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const domains: DomainInfo[] = result.data.map((d: any) => ({
            id: d.id, // Keep the ID as returned by the backend (can be integer, UUID, or string)
            domain: d.domainName,
            description: d.domainDescription
          }));
          // Filter out domains without a domain name (id can be NaN for obfuscated IDs)
          const validDomains = domains.filter(d => d.domain);
          setAvailableDomains(validDomains);

          // Auto-select first domain if none selected, or update existing selection with full data
          if (validDomains.length > 0) {
            if (!selectedDomain || !selectedDomain.domain) {
              // No valid domain selected — pick first valid one
              setSelectedDomain(validDomains[0]);
            } else {
              // Ensure cached selectedDomain is synchronized with fresh API data
              const liveDomainData = validDomains.find(d => String(d.id) === String(selectedDomain.id));
              
              if (liveDomainData) {
                // Update if the cached name or description is outdated
                if (liveDomainData.domain !== selectedDomain.domain || liveDomainData.description !== selectedDomain.description) {
                  setSelectedDomain(liveDomainData);
                }
              } else {
                // If the previously selected domain no longer exists for this tenant, fallback to the first valid one
                setSelectedDomain(validDomains[0]);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading tenant domains:', error);
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleTenantChange = (tenant: TenantInfo) => {
    setSelectedTenant(tenant);
    // Keep menu open to allow domain selection
  };

  const handleDomainChange = (domain: DomainInfo) => {
    setSelectedDomain(domain);
    setShowMenu(false);
  };

  // Calculate menu position based on avatar position
  useEffect(() => {
    if (showMenu && avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.left // Align left edge of dropdown with left edge of avatar
      });
    }
  }, [showMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking inside the avatar button or the dropdown menu
      if (showMenu &&
        !target.closest('.tenant-domain-selector') &&
        !target.closest('.tenant-domain-dropdown') &&
        !avatarRef.current?.contains(target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const hasMultipleTenants = availableTenants.length > 1;
  const hasMultipleDomains = availableDomains.length > 1;

  // Filter tenants and domains based on search query
  const filteredTenants = availableTenants.filter(tenant => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return tenant.name.toLowerCase().includes(query) ||
      tenant.slug.toLowerCase().includes(query);
  });

  const filteredDomains = availableDomains.filter(domain => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return domain.domain.toLowerCase().includes(query) ||
      (domain.description && domain.description.toLowerCase().includes(query));
  });

  // Reset search when menu closes
  useEffect(() => {
    if (!showMenu) {
      setSearchQuery('');
    }
  }, [showMenu]);

  // Hide selector entirely if there is only 1 domain (as requested)
  if (!hasMultipleDomains) {
    return null;
  }

  return (
    <div className="relative tenant-domain-selector flex items-center">
      {/* Clickable Tenant/Domain selector */}
      <SimpleTooltip content={t('common:selectDomain', 'Seleccionar Dominio')} position="bottom">
        <button
          ref={avatarRef}
          onClick={() => !loadingDomains && setShowMenu(!showMenu)}
          disabled={loadingDomains}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span className="truncate max-w-[120px]">{selectedTenant?.name || user?.tenant?.name || t('common.noTenant', 'No Tenant')}</span>
            {selectedDomain && (
              <>
                <span className="text-muted-foreground">-</span>
                <span className="truncate max-w-[120px]">{selectedDomain.domain}</span>
              </>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showMenu ? 'rotate-180' : ''}`} />
        </button>
      </SimpleTooltip>

      {/* Unified Dropdown Menu - Using Portal to escape stacking context */}
      {showMenu && createPortal(
        <div
          className="tenant-domain-dropdown fixed bg-popover border border-border rounded-lg shadow-xl overflow-hidden"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: '320px',
            maxWidth: 'calc(100vw - 2rem)',
            zIndex: 10000
          }}
        >
          {/* Header with Search */}
          <div className="px-4 py-3 bg-muted/50 border-b border-border space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">{t('common.selectContext', 'Select Context')}</span>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('common.search', 'Search...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {/* Tenants Section */}
            {hasMultipleTenants && (
              <div className="p-2 border-b border-border">
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('common.tenant', 'Tenant')} {searchQuery && <span className="text-xs normal-case">({filteredTenants.length})</span>}
                </div>
                {filteredTenants.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                    {t('common.noItemsFound', 'No items found')}
                  </div>
                ) : (
                  filteredTenants.map(tenant => (
                    <button
                      key={tenant.id}
                      onClick={() => handleTenantChange(tenant)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors mb-1 ${selectedTenant?.id === tenant.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent text-foreground'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{tenant.name}</div>
                          <div className="text-xs opacity-75 truncate">{tenant.slug}</div>
                        </div>
                        {selectedTenant?.id === tenant.id && (
                          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-current"></div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Domains Section */}
            <div className="p-2">
              <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Server className="h-3 w-3" />
                {t('common.domains', 'Domains')} {searchQuery && <span className="text-xs normal-case">({filteredDomains.length})</span>} {loadingDomains && <span className="text-xs">({t('common.loading', 'Loading...')})</span>}
              </div>
              {filteredDomains.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {loadingDomains ? t('common.loading', 'Loading...') : (searchQuery ? t('common.noItemsFound', 'No items found') : t('common.noDomains', 'No domains available'))}
                </div>
              ) : (
                filteredDomains.map(domain => (
                  <button
                    key={domain.id}
                    onClick={() => handleDomainChange(domain)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${selectedDomain?.id === domain.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent text-foreground'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <Server className="h-3.5 w-3.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{domain.domain}</div>
                        {domain.description && (
                          <div className="text-xs opacity-75 truncate">{domain.description}</div>
                        )}
                      </div>
                      {selectedDomain?.id === domain.id && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-current"></div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

